import { Link, createFileRoute, notFound } from '@tanstack/react-router'
import { Suspense, lazy, useMemo } from 'react'
import {
  getDocComponentLoader,
  getDocContent,
  getDocExcerpt,
  getDocTitle,
} from '@/features/docs/services/doc-content.service'
import { DocsContent } from '@/features/docs/components/DocsContent'
import { DocsBreadcrumb } from '@/features/docs/components/DocsBreadcrumb'
import { DocsToC } from '@/features/docs/components/DocsToC'
import { DocsCopyButton } from '@/features/docs/components/DocsCopyButton'
import {
  findNavItem,
  getAdjacentItems,
} from '@/features/docs/config/navigation'
import { generateLinks, generateMetaTags } from '@/lib/seo'
import { getBreadcrumbSchema, renderJsonLd } from '@/lib/structured-data'
import { BASE_URL } from '@/lib/constants'

export const Route = createFileRoute('/docs/$')({
  loader: async ({ params }) => {
    const slug = params._splat || 'introduction'
    // Only the raw text + slug go into loaderData — it gets serialized for
    // client hydration, and the compiled MDX component (below, in the
    // component function) is a function reference that can't survive that.
    // Re-deriving it from the same lazy-loaded glob is a single small chunk
    // fetch (see doc-content.service.ts), not a data fetch.
    const { content, error } = await getDocContent(slug)
    if (!content) throw notFound({ data: error })
    const navItem = findNavItem(slug)
    const title = navItem?.item.title ?? getDocTitle(content, slug)
    return { slug, content, navItem, title }
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {}
    const { slug, content, title } = loaderData

    const breadcrumbSchema = getBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Docs', url: '/docs' },
      { name: title, url: `/docs/${slug}` },
    ])

    return {
      meta: generateMetaTags({
        title: `${title} - ModelStack Docs`,
        description: getDocExcerpt(content),
        canonical: `/docs/${slug}`,
      }),
      links: [
        ...generateLinks({ canonical: `/docs/${slug}` }),
        // Lets crawlers/agents that follow standard link-discovery find the
        // raw-markdown version of this page without guessing the URL.
        { rel: 'alternate', type: 'text/markdown', href: `${BASE_URL}/docs/${slug}.md` },
      ],
      scripts: [
        {
          type: 'application/ld+json',
          children: renderJsonLd(breadcrumbSchema as any),
        },
      ],
    }
  },
  notFoundComponent: DocsNotFound,
  component: DocsCatchAllRoute,
})

function DocsContentSkeleton() {
  return (
    <div className="space-y-3 animate-pulse" aria-hidden="true">
      <div className="h-4 w-3/4 rounded-full bg-[var(--color-bg-tertiary)]" />
      <div className="h-4 w-full rounded-full bg-[var(--color-bg-tertiary)]" />
      <div className="h-4 w-5/6 rounded-full bg-[var(--color-bg-tertiary)]" />
    </div>
  )
}

function DocsNotFound() {
  return (
    <div className="flex items-center justify-center min-h-[60vh] px-8">
      <div className="text-center max-w-sm">
        <span className="material-symbols-outlined text-5xl text-[var(--color-text-tertiary)] mb-4 block">
          description
        </span>
        <h1 className="text-xl font-bold text-[var(--color-text-primary)] mb-2">
          Page not found
        </h1>
        <p className="text-sm text-[var(--color-text-secondary)] mb-6">
          The documentation page you're looking for doesn't exist.
        </p>
        <Link
          // @ts-expect-error - Dynamic doc routes not in generated types
          to={'/docs/introduction'}
          className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-[var(--color-primary-foreground)] rounded-full text-sm font-medium transition-all duration-300 ease-brisk hover:bg-primary/90 hover:-translate-y-0.5 hover:shadow-[0_0_30px_var(--color-primary-shadow)] active:scale-[0.97]"
        >
          Back to Docs
        </Link>
      </div>
    </div>
  )
}

function DocsCatchAllRoute() {
  const { slug, content, title } = Route.useLoaderData()
  const { prev, next } = getAdjacentItems(slug)
  // The compiled MDX component isn't in loaderData (see loader comment) —
  // re-derive it here as its own on-demand chunk instead of eagerly bundling
  // every doc article into the client's shared entry chunk.
  const Component = useMemo(() => {
    const loadComponent = getDocComponentLoader(slug)
    return loadComponent ? lazy(loadComponent) : null
  }, [slug])

  return (
    <div className="flex gap-0">
      {/* Main content */}
      <div className="flex-1 min-w-0 px-8 py-10 max-w-3xl">
        <DocsBreadcrumb slug={slug} />

        <div className="flex items-start justify-between gap-4 mb-2">
          <h1 className="text-3xl font-bold text-[var(--color-text-primary)] tracking-[-0.03em] md:text-4xl">
            {title}
          </h1>
          <DocsCopyButton content={content} />
        </div>

        {Component && (
          <Suspense fallback={<DocsContentSkeleton />}>
            <DocsContent Component={Component} />
          </Suspense>
        )}

        {/* Prev/Next navigation */}
        {(prev || next) && (
          <div className="flex items-center justify-between gap-4 mt-12 pt-8 border-t border-[var(--color-border)]">
            {prev ? (
              <Link
                // @ts-expect-error - Dynamic doc routes not in generated types
                to={`/docs/${prev.slug}`}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-[var(--color-border)] hover:border-primary/40 hover:bg-[var(--color-bg-secondary)]/70 hover:backdrop-blur-md hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-24px_var(--color-shadow)] transition-all duration-300 ease-brisk group max-w-[45%]"
              >
                <span className="material-symbols-outlined text-lg text-[var(--color-text-tertiary)] group-hover:text-primary shrink-0">
                  arrow_back
                </span>
                <div className="text-left min-w-0">
                  <div className="text-xs text-[var(--color-text-tertiary)] mb-0.5">
                    Previous
                  </div>
                  <div className="text-sm font-medium text-[var(--color-text-primary)] group-hover:text-primary truncate">
                    {prev.title}
                  </div>
                </div>
              </Link>
            ) : (
              <div />
            )}

            {next && (
              <Link
                // @ts-expect-error - Dynamic doc routes not in generated types
                to={`/docs/${next.slug}`}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-[var(--color-border)] hover:border-primary/40 hover:bg-[var(--color-bg-secondary)]/70 hover:backdrop-blur-md hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-24px_var(--color-shadow)] transition-all duration-300 ease-brisk group ml-auto max-w-[45%]"
              >
                <div className="text-right min-w-0">
                  <div className="text-xs text-[var(--color-text-tertiary)] mb-0.5">
                    Next
                  </div>
                  <div className="text-sm font-medium text-[var(--color-text-primary)] group-hover:text-primary truncate">
                    {next.title}
                  </div>
                </div>
                <span className="material-symbols-outlined text-lg text-[var(--color-text-tertiary)] group-hover:text-primary shrink-0">
                  arrow_forward
                </span>
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Right ToC rail */}
      <div className="hidden xl:block w-56 shrink-0 px-4 py-10">
        <DocsToC content={content} />
      </div>
    </div>
  )
}
